import type { PlatformUser, UserRole, UserStatus } from '../types/user';
import type { Department } from '../types/department';
import type { Company } from '../types/company';

export const departments: Department[] = [
  {
    id: 'dept-1',
    name: 'Engineering',
    description: 'Engineering and Development',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'dept-2',
    name: 'Quality Control',
    description: 'Quality Assurance and Testing',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'dept-3',
    name: 'Project Management',
    description: 'Project Planning and Execution',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'dept-4',
    name: 'Operations',
    description: 'Operations and Maintenance',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'dept-5',
    name: 'Research & Development',
    description: 'Innovation and Research',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

export const companies: Company[] = [
  {
    id: 'comp-1',
    name: 'TechCorp Industries',
    industry: 'Manufacturing',
    website: 'https://techcorp.com',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'comp-2',
    name: 'Global Engineering Solutions',
    industry: 'Engineering',
    website: 'https://globaleng.com',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'comp-3',
    name: 'Quality Systems Inc.',
    industry: 'Quality Control',
    website: 'https://qualitysys.com',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

const firstNames = [
  'James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles',
  'Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen'
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
  'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin'
];

const roles: UserRole[] = ['admin', 'user', 'super_admin'];
const statuses: UserStatus[] = ['active', 'inactive', 'pending', 'suspended'];

export const userGroups: string[] = [
  'user_management',
  'device_management',
  'project_management',
  'system_settings',
  'reporting',
  'audit_logs'
];

const generateEmail = (firstName: string, lastName: string, company: Company) => {
  const companyDomain = company.website?.replace('https://', '').replace('http://', '') || 'company.com';
  return `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${companyDomain}`;
};

const getRandomElement = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

const getRandomElements = <T>(array: T[], count: number): T[] => {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const generateMockUsers = (count: number): PlatformUser[] => {
  return Array.from({ length: count }, (_, index) => {
    const firstName = getRandomElement(firstNames);
    const lastName = getRandomElement(lastNames);
    const company = getRandomElement(companies);
    const department = getRandomElement(departments);
    const role = getRandomElement(roles);
    const status = getRandomElement(statuses);
    const assignedGroups = getRandomElements(userGroups, Math.floor(Math.random() * 4) + 1);

    return {
      id: `user-${index + 1}`,
      name: `${firstName} ${lastName}`,
      email: generateEmail(firstName, lastName, company),
      role,
      status,
      groups: assignedGroups,
      profile: {
        department,
        company,
        phoneNumber: `+1${Math.floor(Math.random() * 1000000000)}`,
        title: role === 'admin' ? 'Senior Manager' : 'Staff Member',
        location: 'Remote',
        timezone: 'UTC',
        lastPasswordChange: new Date(Date.now() - Math.floor(Math.random() * 90 * 24 * 60 * 60 * 1000)).toISOString(),
        failedLoginAttempts: Math.floor(Math.random() * 3)
      },
      createdAt: new Date(Date.now() - Math.floor(Math.random() * 90 * 24 * 60 * 60 * 1000)).toISOString(),
      lastLogin: new Date(Date.now() - Math.floor(Math.random() * 30 * 24 * 60 * 60 * 1000)).toISOString(),
      requirePasswordChange: Math.random() > 0.8,
      twoFactorEnabled: Math.random() > 0.7,
      sessionTimeout: 30
    };
  });
};

// Generate 50 random users
const generatedUsers = generateMockUsers(50);

// Ensure we have a super admin
generatedUsers[0] = {
  id: 'user-1',
  name: 'Super Admin',
  email: 'super.admin@admin.com',
  role: 'super_admin',
  status: 'active',
  groups: userGroups,
  profile: {
    department: departments[0],
    company: companies[0],
    title: 'System Administrator',
    location: 'HQ',
    timezone: 'UTC',
    lastPasswordChange: new Date().toISOString(),
    failedLoginAttempts: 0
  },
  createdAt: '2024-01-01T00:00:00Z',
  lastLogin: new Date().toISOString(),
  requirePasswordChange: false,
  twoFactorEnabled: true,
  sessionTimeout: 60
};

export const mockUsers = generatedUsers; 